import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MailingChannel } from '../mailing-constructor/mailingTypes';
import { CHANNEL_META } from '../mailing-constructor/mailingTypes';

export type ScenarioPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  channels: MailingChannel[];
  primaryLabel: string;
  onPrimary: () => void;
  primaryLoading?: boolean;
  primaryDisabled?: boolean;
  /** Вторая кнопка (например, создание серверной цепочки из шаблона каскада). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  secondaryLoading?: boolean;
  secondaryDisabled?: boolean;
};

const ScenarioPreviewDialog: React.FC<ScenarioPreviewDialogProps> = ({
  open,
  onClose,
  title,
  description,
  channels,
  primaryLabel,
  onPrimary,
  primaryLoading = false,
  primaryDisabled = false,
  secondaryLabel,
  onSecondary,
  secondaryLoading = false,
  secondaryDisabled = false,
}) => {
  const { t } = useTranslation();
  const showSecondary = Boolean(secondaryLabel && onSecondary);
  const secondaryPart = showSecondary && secondaryLabel ? t('scenarioPreview.hintSecondary', { label: secondaryLabel }) : '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>{title}</DialogTitle>
      <DialogContent>
        {description.trim() ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            {description}
          </Typography>
        ) : null}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {t('scenarioPreview.channelChainTitle')}
        </Typography>
        {channels.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('scenarioPreview.noChannelsOnCanvas')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
            {channels.map((ch, i) => (
              <React.Fragment key={`${ch}-${i}`}>
                {i > 0 ? (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    →
                  </Typography>
                ) : null}
                <Chip
                  size="small"
                  label={`${i + 1}. ${t(`channels.${ch}`)}`}
                  sx={{ bgcolor: CHANNEL_META[ch].color, color: '#fff', fontWeight: 600 }}
                />
              </React.Fragment>
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
          {t('scenarioPreview.hintFooter', {
            primary: primaryLabel,
            secondaryPart,
          })}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} color="inherit">
          {t('scenarioPreview.close')}
        </Button>
        {showSecondary ? (
          <Button
            variant="outlined"
            onClick={onSecondary}
            disabled={secondaryDisabled || secondaryLoading || primaryLoading}
            startIcon={secondaryLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {secondaryLoading ? t('common.creating') : secondaryLabel}
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={onPrimary}
          disabled={primaryDisabled || primaryLoading || (showSecondary && secondaryLoading)}
          startIcon={primaryLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {primaryLoading ? t('common.wait') : primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioPreviewDialog;
